import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
    alpha,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArchiveIcon from "@mui/icons-material/Inventory2Outlined";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import EditIcon from "@mui/icons-material/Edit";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import ReactCountryFlag from "react-country-flag";

import { COUNTRIES } from "../constants/countries";
import { LANGUAGES } from "../constants/languages";
import { SettingsContext } from "../context/SettingsContext";
import {
    createWorkerRequest,
    deleteWorkerRequest,
    fetchWorkerByIdRequest,
    updateWorkerRequest,
} from "../api/workersApi";
import { usersApi } from "../api/usersApi";
import { User, Worker, WorkerAvailabilityStatus, WorkerStatus } from "../models/worker";
import { extractErrorMessageSync } from "../utils/errorHandler";
import { getWorkerStatusColor } from "../utils/workerStatusColor";

import ConfirmationModal from "../components/ConfirmationModel";
import WorkerMemo from "../components/Worker/WorkerMemo";
import WorkerExperience from "../components/Worker/WorkerExperience";
import WorkerCertReview from "../components/Worker/WorkerCertReview";
import { WorkerFileManager } from "../components/Worker/WorkerFileManager";
import { NawDocumentSection } from "../components/Worker/NawDocumentSection";
import { NawFieldsEditor } from "../components/Worker/NawFieldsEditor";
import { DocumentExpiryBanner } from "../components/Worker/DocumentExpiryBanner";
import { WorkerQuestionForm } from "../components/Worker/QuestionForm";
import IdentificationDocuments from "../components/Worker/IdentificationDocuments";
import WorkerCertifications from "../components/Worker/WorkerCertifications";
import EasyflexProfilePanel from "../components/Worker/EasyflexProfilePanel";
import EasyflexDocumentsTab from "../components/Worker/EasyflexDocumentsTab";
import CommunicationChannelsList from "../components/Worker/CommunicationChannelsList";
import CVEditorModal from "../components/Worker/CVEditorModal";
import CandidateConfirmationDialog from "../components/Worker/CandidateConfirmationDialog";
import { RecruitmentPreferencesSection } from "../components/Worker/RecruitmentPreferencesSection";
import { LazyTabPanel as TabPanel } from "../components/Worker/LazyTabPanel";
import { WorkerAuditTimeline } from "../components/Worker/WorkerAuditTimeline";
import { WorkerContactTimeline } from "../components/Worker/WorkerContactTimeline";
import { WorkerFeedbackPanel } from "../components/Worker/WorkerFeedbackPanel";
import { WorkerDetailTabs, WORKER_DETAIL_TAB } from "../components/Worker/WorkerDetailTabs";
import { WorkerStatusEditor } from "../components/Worker/WorkerStatusEditor";
import { CandidatePromotionAction } from "../components/Worker/CandidatePromotionAction";
import { WorkerSaleslistPushDialog } from "../components/Worker/WorkerSaleslistPushDialog";
import { CandidatePublicationStatusAlert } from "../components/Worker/CandidatePublicationStatusAlert";
import { useUserHasRole } from "../utils/authUtils";
import { RECRUITMENT_MANAGE_WORKERS_ROLE } from "../constants/authRoles";
import {
    EMPTY_WORKER_FORM,
    recruitmentPreferencesPayload,
    WorkerFormState,
    workerToFormState,
} from "../components/Worker/workerDetailForm";
import { WorkerDetailRequestFence } from "../components/Worker/workerDetailRequestFence";

type Country = (typeof COUNTRIES)[number];
type Language = (typeof LANGUAGES)[number];

const getCountryForNationalityValue = (value: string): Country | null => {
    const trimmed = (value || "").trim();
    if (!trimmed) return null;
    if (/^[A-Za-z]{2}$/.test(trimmed)) {
        return COUNTRIES.find((c) => c.code.toUpperCase() === trimmed.toUpperCase()) ?? null;
    }
    return COUNTRIES.find((c) => c.nationality.toLowerCase() === trimmed.toLowerCase()) ?? null;
};

const getLanguageFlagCountry = (langCode: string): string => {
    const c = (langCode || "").toLowerCase();
    const map: Record<string, string> = {
        en: "GB",
        de: "DE",
        nl: "NL",
        pl: "PL",
        ro: "RO",
        fr: "FR",
        es: "ES",
        it: "IT",
        cs: "CZ",
        hu: "HU",
        sk: "SK",
        sl: "SI",
        hr: "HR",
        tr: "TR",
        uk: "UA",
        ru: "RU",
        zh: "CN",
        "zh-cn": "CN",
        "zh-tw": "TW",
    };
    return map[c] || map[c.split("-")[0]] || c.toUpperCase();
};

const formatUserLabel = (user: User) => {
    const name = `${user.firstName || ""} ${user.lastName || ""}`.trim();
    return name || user.username || user.email || `User ${user.id}`;
};

const WorkerDetail: React.FC = () => {
    const { workerId } = useParams<{ workerId: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const { settings } = useContext(SettingsContext);
    const canManageWorkers = useUserHasRole(RECRUITMENT_MANAGE_WORKERS_ROLE);
    const workerRequestFence = useRef(new WorkerDetailRequestFence(workerId ?? null));
    const previousCanManageWorkers = useRef(canManageWorkers);
    workerRequestFence.current.activate(workerId ?? null);

    const isCreateMode = useMemo(() => new URLSearchParams(location.search).get("mode") === "create", [location.search]);

    const [worker, setWorker] = useState<Worker | null>(null);
    const [loading, setLoading] = useState(true);

    const [isNewWorker, setIsNewWorker] = useState(isCreateMode);
    const [editMode, setEditMode] = useState(isCreateMode);
    const editingAllowed = editMode && canManageWorkers;
    const [saving, setSaving] = useState(false);
    const [showCVEditor, setShowCVEditor] = useState(false);

    const [formData, setFormData] = useState<WorkerFormState>(EMPTY_WORKER_FORM);
    const [originalFormData, setOriginalFormData] = useState<WorkerFormState>(EMPTY_WORKER_FORM);
    const originalFormDataRef = useRef(originalFormData);
    originalFormDataRef.current = originalFormData;
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});

    const [users, setUsers] = useState<User[]>([]);
    const recruiters = useMemo(() => users.filter((u) => u.role === "RECRUITER"), [users]);
    const accountManagers = useMemo(() => users.filter((u) => u.role === "ACCOUNTMANAGER"), [users]);

    const [tab, setTab] = useState(0);
    // Bumped after a side-channel write (e.g. NAW PDF generated) so
    // WorkerFileManager re-fetches without a page reload.
    const [filesRefreshKey, setFilesRefreshKey] = useState(0);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showCandidateDialog, setShowCandidateDialog] = useState(false);
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: "success" | "error" | "warning" | "info";
    }>({ open: false, message: "", severity: "info" });

    const [showSaleslistDialog, setShowSaleslistDialog] = useState(false);

    const nationalityCountry = useMemo(() => getCountryForNationalityValue(formData.nationality), [formData.nationality]);
    const selectedRecruiter = useMemo(
        () => (formData.recruiterId ? recruiters.find((u) => u.id === formData.recruiterId) ?? null : null),
        [formData.recruiterId, recruiters]
    );
    const selectedAccountManager = useMemo(
        () => (formData.accountManagerId ? accountManagers.find((u) => u.id === formData.accountManagerId) ?? null : null),
        [formData.accountManagerId, accountManagers]
    );
    const selectedProfession = useMemo(
        () => settings?.professions.find((p) => p.professionKey === formData.professionKey) ?? null,
        [formData.professionKey, settings?.professions]
    );

    const displayName = useMemo(() => {
        const parts = [formData.firstName, formData.prefix, formData.middleName, formData.lastName]
            .map((p) => (p || "").trim())
            .filter(Boolean);
        const name = parts.join(" ").trim();
        if (name) return name;
        if (isNewWorker) return "New worker";
        return worker?.fullName || "Worker";
    }, [formData.firstName, formData.lastName, formData.middleName, formData.prefix, isNewWorker, worker?.fullName]);

    const hasUnsavedChanges = useMemo(() => {
        if (!editingAllowed) return false;
        return JSON.stringify(formData) !== JSON.stringify(originalFormData);
    }, [editingAllowed, formData, originalFormData]);

    const setField = useCallback(<K extends keyof WorkerFormState>(key: K, value: WorkerFormState[K]) => {
        setFormData((prev) => ({ ...prev, [key]: value }));
        setFormErrors((prev) => {
            if (!prev[key as string]) return prev;
            const next = { ...prev };
            delete next[key as string];
            return next;
        });
    }, []);

    // Handler for status changes - shows confirmation dialog when promoting to CANDIDATE
    const handleStatusChange = useCallback((newStatus: WorkerStatus) => {
        // If changing to CANDIDATE from a different status, show confirmation dialog
        if (newStatus === WorkerStatus.CANDIDATE && formData.status !== WorkerStatus.CANDIDATE) {
            setShowCandidateDialog(true);
        } else {
            setField("status", newStatus);
        }
    }, [formData.status, setField]);

    const handleCandidateDialogClose = useCallback(() => {
        setShowCandidateDialog(false);
    }, []);

    const loadWorker = useCallback(async () => {
        if (!workerId) return;
        const ticket = workerRequestFence.current.beginLoad(workerId);
        setLoading(true);
        try {
            const fetchedWorker = await fetchWorkerByIdRequest(workerId);
            if (!workerRequestFence.current.accept(ticket)) return;
            setWorker(fetchedWorker);

            const nextForm = workerToFormState(fetchedWorker);
            setFormData(nextForm);
            setOriginalFormData(nextForm);
        } catch (error) {
            if (!workerRequestFence.current.isCurrent(ticket)) return;
            const message = extractErrorMessageSync(error);
            setSnackbar({ open: true, message, severity: "error" });
            setWorker(null);
        } finally {
            if (workerRequestFence.current.isCurrent(ticket)) setLoading(false);
        }
    }, [workerId]);

    // Handler for confirming candidate promotion with availableAt date
    const handleCandidateConfirm = useCallback(async (availableAt: string) => {
        if (!workerId || !canManageWorkers) return;
        const mutationTicket = workerRequestFence.current.beginMutation(workerId);
        if (!mutationTicket) return;
        try {
            setSaving(true);
            await updateWorkerRequest({
                workerId,
                status: WorkerStatus.CANDIDATE,
                availableAt,
            });
            if (!workerRequestFence.current.isCurrent(mutationTicket)) return;
            setSnackbar({
                open: true,
                message: "Candidate promoted; Saleslist publication queued",
                severity: "info",
            });
            setSaving(false);
            setShowCandidateDialog(false);
            await loadWorker();
        } catch (error) {
            if (!workerRequestFence.current.isCurrent(mutationTicket)) return;
            const message = extractErrorMessageSync(error);
            setSnackbar({ open: true, message, severity: "error" });
        } finally {
            if (workerRequestFence.current.isCurrent(mutationTicket)) {
                setSaving(false);
                setShowCandidateDialog(false);
            }
        }
    }, [canManageWorkers, workerId, loadWorker]);

    useEffect(() => {
        const previouslyAllowed = previousCanManageWorkers.current;
        previousCanManageWorkers.current = canManageWorkers;
        const mayCreate = isCreateMode && canManageWorkers;
        setIsNewWorker(mayCreate);
        setEditMode(mayCreate);
        if (!canManageWorkers) {
            workerRequestFence.current.invalidate();
            setFormData(originalFormDataRef.current);
            setFormErrors({});
            setSaving(false);
            setShowCandidateDialog(false);
            setShowDeleteModal(false);
            setShowSaleslistDialog(false);
        } else if (!previouslyAllowed && !isCreateMode && workerId) {
            void loadWorker();
        }
    }, [canManageWorkers, isCreateMode, loadWorker, workerId]);

    useEffect(() => {
        if (!workerId) return;

        setWorker(null);
        setFormData(EMPTY_WORKER_FORM);
        setOriginalFormData(EMPTY_WORKER_FORM);
        setFormErrors({});
        setSaving(false);
        setShowCandidateDialog(false);
        setShowSaleslistDialog(false);
        setTab(WORKER_DETAIL_TAB.OVERVIEW);

        if (isCreateMode) {
            setLoading(false);
            return;
        }

        const requestFence = workerRequestFence.current;
        void loadWorker();
        return () => requestFence.invalidate();
    }, [isCreateMode, loadWorker, workerId]);

    useEffect(() => {
        const loadUsers = async () => {
            try {
                await usersApi.syncUsersFromKeycloak();
                const recruitmentUsers = await usersApi.getRecruitmentUsers();
                setUsers(recruitmentUsers);
            } catch {
                // Non-blocking
            }
        };
        loadUsers();
    }, []);

    const validateForm = () => {
        const errors: Record<string, string> = {};

        if (!formData.phoneNumber.trim()) {
            errors.phoneNumber = "Phone number is required";
        } else if (!formData.phoneNumber.match(/^\+[1-9]\d{1,14}$/)) {
            errors.phoneNumber = "Use international format starting with + (e.g., +31612345678)";
        }

        if (!formData.firstName.trim() && !formData.lastName.trim()) {
            errors.firstName = "At least first or last name is required";
        }

        if (formData.emailAddress.trim()) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(formData.emailAddress)) errors.emailAddress = "Enter a valid email address";
        }

        if (!formData.isCustomOccupation && !formData.professionKey) {
            errors.professionKey = "Profession is required";
        }
        if (formData.isCustomOccupation && !formData.customOccupation.trim()) {
            errors.customOccupation = "Custom occupation is required";
        }

        if (!formData.status) errors.status = "Status is required";

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSave = async () => {
        if (!workerId) return;
        if (!canManageWorkers) {
            setSnackbar({ open: true, message: "You no longer have permission to edit workers.", severity: "error" });
            setEditMode(false);
            return;
        }
        if (!isNewWorker && !workerRequestFence.current.canSave(workerId)) {
            setSnackbar({ open: true, message: "Worker details changed while loading. Refresh before saving.", severity: "error" });
            return;
        }
        if (!validateForm()) {
            setSnackbar({ open: true, message: "Please fix the highlighted fields.", severity: "error" });
            return;
        }

        const mutationTicket = isNewWorker
            ? workerRequestFence.current.beginOperation(workerId)
            : workerRequestFence.current.beginMutation(workerId);
        if (!mutationTicket) return;
        setSaving(true);
        try {
            if (isNewWorker) {
                const createData: any = {
                    workerId,
                    firstName: formData.firstName || "",
                    lastName: formData.lastName || "",
                    middleName: formData.middleName || undefined,
                    prefix: formData.prefix || undefined,
                    emailAddress: formData.emailAddress || undefined,
                    phoneNumber: formData.phoneNumber || undefined,
                    nationality: formData.nationality || undefined,
                    dateOfBirth: formData.dateOfBirth || undefined,
                    professionKey: formData.isCustomOccupation ? "OTHER" : formData.professionKey || "",
                    customOccupation: formData.isCustomOccupation ? formData.customOccupation : undefined,
                    isCustomOccupation: formData.isCustomOccupation,
                    status: formData.status || WorkerStatus.INCOMPLETE,
                    workerAvailability: formData.workerAvailability || WorkerAvailabilityStatus.UNAVAILABLE,
                    ...recruitmentPreferencesPayload(formData),
                };

                const created = await createWorkerRequest(createData);
                if (!workerRequestFence.current.isCurrent(mutationTicket)) return;
                setWorker(created);
                setIsNewWorker(false);
                setEditMode(false);
                setSaving(false);
                navigate(location.pathname, { replace: true });
                setSnackbar({ open: true, message: "Worker created successfully.", severity: "success" });
                return;
            }

            const updated = await updateWorkerRequest({
                workerId,
                firstName: formData.firstName || null,
                lastName: formData.lastName || null,
                middleName: formData.middleName || null,
                prefix: formData.prefix || null,
                emailAddress: formData.emailAddress || undefined,
                phoneNumber: formData.phoneNumber,
                nationality: formData.nationality || undefined,
                dateOfBirth: formData.dateOfBirth || undefined,
                professionKey: formData.isCustomOccupation ? null : formData.professionKey || null,
                customOccupation: formData.isCustomOccupation ? formData.customOccupation : null,
                isCustomOccupation: formData.isCustomOccupation,
                status: formData.status,
                workerAvailability: formData.workerAvailability,
                recruiterId: formData.recruiterId,
                accountManagerId: formData.accountManagerId,
                memo: formData.memo,
                languages: formData.languages,
                ...recruitmentPreferencesPayload(formData, worker?.homeAddress),
            });

            if (!workerRequestFence.current.isCurrent(mutationTicket)) return;
            setWorker(updated);
            const nextForm = workerToFormState(updated);
            setFormData(nextForm);
            setOriginalFormData(nextForm);
            setEditMode(false);
            setSnackbar({ open: true, message: "Changes saved.", severity: "success" });
        } catch (error) {
            if (!workerRequestFence.current.isCurrent(mutationTicket)) return;
            const message = extractErrorMessageSync(error);
            setSnackbar({ open: true, message, severity: "error" });
        } finally {
            if (workerRequestFence.current.isCurrent(mutationTicket)) {
                setSaving(false);
            }
        }
    };

    const handleCancel = () => {
        setFormData(originalFormData);
        setFormErrors({});
        setEditMode(false);
    };

    const handleDelete = async () => {
        if (!workerId || !canManageWorkers) return;
        const operationTicket = workerRequestFence.current.beginMutation(workerId);
        if (!operationTicket) return;
        try {
            await deleteWorkerRequest(workerId);
            if (!workerRequestFence.current.isCurrent(operationTicket)) return;
            setSnackbar({ open: true, message: "Worker deleted.", severity: "success" });
            navigate("/resources/workers");
        } catch (error) {
            if (!workerRequestFence.current.isCurrent(operationTicket)) return;
            const message = extractErrorMessageSync(error);
            setSnackbar({ open: true, message, severity: "error" });
        }
    };

    const handleToggleArchive = async () => {
        if (!workerId || !worker || !canManageWorkers) return;
        const mutationTicket = workerRequestFence.current.beginMutation(workerId);
        if (!mutationTicket) return;
        const next = !worker.archived;
        try {
            const updated = await updateWorkerRequest({ workerId, archived: next });
            if (!workerRequestFence.current.isCurrent(mutationTicket)) return;
            setWorker((current) => current ? { ...current, archived: updated.archived ?? next } : current);
            setSnackbar({
                open: true,
                message: next ? "Worker archived — hidden from lists." : "Worker un-archived.",
                severity: "success",
            });
        } catch (error) {
            if (!workerRequestFence.current.isCurrent(mutationTicket)) return;
            setSnackbar({ open: true, message: extractErrorMessageSync(error), severity: "error" });
        }
    };

    const statusChipColor = (status: WorkerStatus) => {
        return getWorkerStatusColor(status, theme.customTokens.status, theme.palette.text.secondary);
    };

    const availabilityChipColor = (availability: WorkerAvailabilityStatus) => {
        const tokens = theme.customTokens.status;
        return availability === WorkerAvailabilityStatus.AVAILABLE ? tokens.available : tokens.unavailable;
    };

    const disabledPanel = (title: string, description: string) => (
        <Paper
            variant="outlined"
            sx={{
                p: 2,
                borderRadius: 2,
                borderColor: alpha(theme.palette.divider, theme.palette.mode === "dark" ? 0.4 : 1),
                backgroundColor: alpha(theme.palette.action.hover, theme.palette.mode === "dark" ? 0.25 : 0.5),
            }}
        >
            <Typography variant="subtitle1" fontWeight={800}>
                {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {description}
            </Typography>
        </Paper>
    );

    if (!workerId) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Alert severity="error">Missing workerId in route.</Alert>
            </Container>
        );
    }

    if (isCreateMode && !canManageWorkers) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Alert severity="error">You do not have permission to create workers.</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 } }}>
            <Stack spacing={2} sx={{ minHeight: 0 }}>
                <Paper
                    variant="outlined"
                    sx={{
                        position: "relative",
                        overflow: "hidden",
                        borderRadius: 3,
                        p: { xs: 2, md: 2.5 },
                        borderColor: alpha(theme.palette.divider, theme.palette.mode === "dark" ? 0.4 : 1),
                    }}
                >
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            background:
                                theme.palette.mode === "dark"
                                    ? `radial-gradient(900px circle at 10% -20%, ${alpha(theme.palette.primary.main, 0.25)} 0%, transparent 55%),
                                       radial-gradient(700px circle at 90% 0%, ${alpha(theme.palette.secondary.main, 0.18)} 0%, transparent 55%)`
                                    : `radial-gradient(900px circle at 10% -20%, ${alpha(theme.palette.primary.main, 0.18)} 0%, transparent 55%),
                                       radial-gradient(700px circle at 90% 0%, ${alpha(theme.palette.secondary.main, 0.14)} 0%, transparent 55%)`,
                            pointerEvents: "none",
                        }}
                    />

                    <Stack spacing={2} sx={{ position: "relative" }}>
                        <Stack
                            direction={{ xs: "column", md: "row" }}
                            alignItems={{ xs: "flex-start", md: "center" }}
                            justifyContent="space-between"
                            spacing={2}
                        >
                            <Stack direction="row" spacing={1.25} alignItems="center">
                                <Tooltip title="Back to workers">
                                    <IconButton onClick={() => navigate("/resources/workers")} size={isMobile ? "small" : "medium"}>
                                        <ArrowBackIcon />
                                    </IconButton>
                                </Tooltip>

                                <Box>
                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                        <Typography variant="h5" fontWeight={900} letterSpacing={-0.3}>
                                            {displayName}
                                        </Typography>
                                        {isNewWorker && (
                                            <Chip
                                                size="small"
                                                label="Draft"
                                                sx={{
                                                    fontWeight: 800,
                                                    backgroundColor: alpha(theme.palette.warning.main, theme.palette.mode === "dark" ? 0.25 : 0.12),
                                                }}
                                            />
                                        )}
                                        {!isNewWorker && (
                                            <>
                                                <Chip
                                                    size="small"
                                                    label={formData.status}
                                                    sx={{
                                                        fontWeight: 600,
                                                        borderRadius: 999,
                                                        color: statusChipColor(formData.status),
                                                        backgroundColor: alpha(statusChipColor(formData.status), theme.palette.mode === "dark" ? 0.25 : 0.12),
                                                    }}
                                                />
                                                <Chip
                                                    size="small"
                                                    label={formData.workerAvailability}
                                                    sx={{
                                                        fontWeight: 600,
                                                        borderRadius: 999,
                                                        color: availabilityChipColor(formData.workerAvailability),
                                                        backgroundColor: alpha(
                                                            availabilityChipColor(formData.workerAvailability),
                                                            theme.palette.mode === "dark" ? 0.25 : 0.12
                                                        ),
                                                    }}
                                                />
                                            </>
                                        )}
                                        {hasUnsavedChanges && (
                                            <Chip
                                                size="small"
                                                label="Unsaved changes"
                                                color="warning"
                                                variant="outlined"
                                                sx={{ fontWeight: 800 }}
                                            />
                                        )}
                                    </Stack>
                                    <Typography variant="body2" color="text.secondary">
                                        Worker ID: <Box component="span" sx={{ fontFamily: "monospace" }}>{workerId}</Box>
                                    </Typography>
                                </Box>
                            </Stack>

                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                <Tooltip title="Refresh">
                                    <span>
                                        <IconButton
                                            aria-label="Refresh worker"
                                            onClick={loadWorker}
                                            disabled={loading || isNewWorker}
                                            size={isMobile ? "small" : "medium"}
                                            color="primary"
                                        >
                                            <RefreshIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>

                                {!editingAllowed ? (
                                    <>
                                        {!isNewWorker && worker && canManageWorkers && (
                                            <CandidatePromotionAction
                                                status={worker.status}
                                                readiness={worker.candidateReadiness}
                                                disabled={loading || saving}
                                                onPromote={() => handleStatusChange(WorkerStatus.CANDIDATE)}
                                            />
                                        )}

                                        {canManageWorkers && (
                                            <Button
                                                variant="outlined"
                                                startIcon={<EditIcon />}
                                                onClick={() => {
                                                    setEditMode(true);
                                                    setTab(0);
                                                }}
                                                disabled={loading}
                                                sx={{ borderRadius: 999 }}
                                            >
                                                Edit
                                            </Button>
                                        )}

                                        <Button
                                            variant="outlined"
                                            startIcon={<PictureAsPdfIcon />}
                                            onClick={() => setShowCVEditor(true)}
                                            disabled={loading || isNewWorker}
                                            sx={{ borderRadius: 999 }}
                                        >
                                            CV Editor
                                        </Button>

                                        {canManageWorkers && (
                                            <Button
                                                variant="outlined"
                                                startIcon={<SendIcon />}
                                                onClick={() => setShowSaleslistDialog(true)}
                                                disabled={loading || isNewWorker || !worker?.emailAddress}
                                                sx={{ borderRadius: 999 }}
                                            >
                                                Saleslist
                                            </Button>
                                        )}

                                        {canManageWorkers && (
                                            <>
                                                <Tooltip title={worker?.archived ? "Un-archive this worker" : "Archive this worker (hide from all lists)"}>
                                                    <span>
                                                        <IconButton
                                                            aria-label={worker?.archived ? "Un-archive this worker" : "Archive this worker"}
                                                            color={worker?.archived ? "primary" : "default"}
                                                            onClick={handleToggleArchive}
                                                            disabled={loading || isNewWorker}
                                                        >
                                                            {worker?.archived ? <UnarchiveIcon /> : <ArchiveIcon />}
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>

                                                <Tooltip title="Delete worker">
                                                    <span>
                                                        <IconButton
                                                            aria-label="Delete worker"
                                                            color="error"
                                                            onClick={() => setShowDeleteModal(true)}
                                                            disabled={loading || isNewWorker}
                                                        >
                                                            <DeleteOutlineIcon />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <Button
                                            variant="outlined"
                                            startIcon={<CloseIcon />}
                                            onClick={handleCancel}
                                            disabled={saving}
                                            sx={{ borderRadius: 999 }}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="contained"
                                            startIcon={<SaveIcon />}
                                            onClick={handleSave}
                                            disabled={saving}
                                            sx={{ borderRadius: 999 }}
                                        >
                                            {isNewWorker ? (saving ? "Creating…" : "Create") : saving ? "Saving…" : "Save"}
                                        </Button>
                                    </>
                                )}
                            </Stack>
                        </Stack>

                        {!loading && !isNewWorker && worker && (
                            <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ flexWrap: "wrap" }}>
                                {selectedRecruiter && (
                                    <Chip
                                        size="small"
                                        label={`Recruiter: ${formatUserLabel(selectedRecruiter)}`}
                                        variant="outlined"
                                    />
                                )}
                                {selectedAccountManager && (
                                    <Chip
                                        size="small"
                                        label={`Account manager: ${formatUserLabel(selectedAccountManager)}`}
                                        variant="outlined"
                                    />
                                )}
                                <Chip size="small" label={`Updated: ${new Date(worker.updatedAt).toLocaleString()}`} variant="outlined" />
                            </Stack>
                        )}
                    </Stack>
                </Paper>

                {!loading && !isNewWorker && worker?.status === WorkerStatus.CANDIDATE && canManageWorkers && (
                    <CandidatePublicationStatusAlert workerId={workerId} canManageWorkers />
                )}

                <WorkerDetailTabs
                    value={tab}
                    onChange={setTab}
                    disabled={isNewWorker}
                    mobile={isMobile}
                    canManageWorkers={canManageWorkers}
                />

                {loading ? (
                    <Paper
                        variant="outlined"
                        sx={{
                            flex: 1,
                            minHeight: 240,
                            borderRadius: 2,
                            display: "grid",
                            placeItems: "center",
                            borderColor: alpha(theme.palette.divider, theme.palette.mode === "dark" ? 0.4 : 1),
                        }}
                    >
                        <CircularProgress />
                    </Paper>
                ) : (
                    <>
                        <TabPanel value={tab} index={0}>
                            <Grid container spacing={2}>
                                {/* Left column - Personal info */}
                                <Grid item xs={12} md={6}>
                                    <Stack spacing={2}>
                                        <Paper
                                            variant="outlined"
                                            sx={{
                                                p: 2,
                                                borderRadius: 2,
                                                borderColor: alpha(theme.palette.divider, theme.palette.mode === "dark" ? 0.4 : 1),
                                            }}
                                        >
                                            <Typography variant="subtitle1" fontWeight={900}>
                                                Personal
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                Core identity and contact information.
                                            </Typography>

                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        label="First name"
                                                        value={formData.firstName}
                                                        onChange={(e) => setField("firstName", e.target.value)}
                                                        fullWidth
                                                        size="small"
                                                        error={Boolean(formErrors.firstName)}
                                                        helperText={formErrors.firstName}
                                                        InputProps={{ readOnly: !editingAllowed }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        label="Last name"
                                                        value={formData.lastName}
                                                        onChange={(e) => setField("lastName", e.target.value)}
                                                        fullWidth
                                                        size="small"
                                                        InputProps={{ readOnly: !editingAllowed }}
                                                    />
                                                </Grid>
                                                <Grid item xs={6} sm={3}>
                                                    <TextField
                                                        label="Prefix"
                                                        value={formData.prefix}
                                                        onChange={(e) => setField("prefix", e.target.value)}
                                                        fullWidth
                                                        size="small"
                                                        InputProps={{ readOnly: !editingAllowed }}
                                                    />
                                                </Grid>
                                                <Grid item xs={6} sm={3}>
                                                    <TextField
                                                        label="Middle name"
                                                        value={formData.middleName}
                                                        onChange={(e) => setField("middleName", e.target.value)}
                                                        fullWidth
                                                        size="small"
                                                        InputProps={{ readOnly: !editingAllowed }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        label="Phone (E.164)"
                                                        value={formData.phoneNumber}
                                                        onChange={(e) => setField("phoneNumber", e.target.value)}
                                                        fullWidth
                                                        size="small"
                                                        error={Boolean(formErrors.phoneNumber)}
                                                        helperText={formErrors.phoneNumber || "Example: +31612345678"}
                                                        InputProps={{ readOnly: !editingAllowed }}
                                                    />
                                                </Grid>

                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        label="Email"
                                                        value={formData.emailAddress}
                                                        onChange={(e) => setField("emailAddress", e.target.value)}
                                                        fullWidth
                                                        size="small"
                                                        error={Boolean(formErrors.emailAddress)}
                                                        helperText={formErrors.emailAddress}
                                                        InputProps={{ readOnly: !editingAllowed }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        label="Date of birth"
                                                        type="date"
                                                        value={formData.dateOfBirth}
                                                        onChange={(e) => setField("dateOfBirth", e.target.value)}
                                                        fullWidth
                                                        size="small"
                                                        InputLabelProps={{ shrink: true }}
                                                        InputProps={{ readOnly: !editingAllowed }}
                                                    />
                                                </Grid>

                                                <Grid item xs={12}>
                                                    {editingAllowed ? (
                                                        <Autocomplete
                                                            size="small"
                                                            options={COUNTRIES}
                                                            value={nationalityCountry}
                                                            getOptionLabel={(option) => `${option.nationality} — ${option.name}`}
                                                            onChange={(_e, option) => setField("nationality", option ? option.code : "")}
                                                            renderOption={(props, option) => (
                                                                <Box component="li" {...props} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                                    <ReactCountryFlag
                                                                        countryCode={option.code}
                                                                        svg
                                                                        cdnUrl="/flags/4x3/"
                                                                        cdnSuffix="svg"
                                                                        style={{ width: "1em", height: "1em" }}
                                                                    />
                                                                    <Box sx={{ display: "flex", gap: 1, alignItems: "baseline" }}>
                                                                        <Typography variant="body2" fontWeight={700}>
                                                                            {option.nationality}
                                                                        </Typography>
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            {option.name}
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                            )}
                                                            renderInput={(params) => <TextField {...params} label="Nationality" fullWidth />}
                                                        />
                                                    ) : (
                                                        <TextField
                                                            label="Nationality"
                                                            value={nationalityCountry?.nationality || formData.nationality || ""}
                                                            fullWidth
                                                            size="small"
                                                            InputProps={{ readOnly: true }}
                                                        />
                                                    )}
                                                </Grid>

                                                <Grid item xs={12}>
                                                    {editingAllowed ? (
                                                        <Autocomplete
                                                            multiple
                                                            size="small"
                                                            options={LANGUAGES}
                                                            value={LANGUAGES.filter((l) => (formData.languages || []).includes(l.code))}
                                                            getOptionLabel={(option) => `${option.name} (${option.nativeName})`}
                                                            onChange={(_e, newValue: Language[]) =>
                                                                setField(
                                                                    "languages",
                                                                    newValue.map((l) => l.code.toLowerCase())
                                                                )
                                                            }
                                                            filterOptions={(options, { inputValue }) =>
                                                                options.filter(
                                                                    (option) =>
                                                                        option.name.toLowerCase().includes(inputValue.toLowerCase()) ||
                                                                        option.nativeName.toLowerCase().includes(inputValue.toLowerCase()) ||
                                                                        option.code.toLowerCase().includes(inputValue.toLowerCase())
                                                                )
                                                            }
                                                            renderOption={(props, option) => (
                                                                <Box component="li" {...props} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                                    <ReactCountryFlag
                                                                        countryCode={getLanguageFlagCountry(option.code)}
                                                                        svg
                                                                        cdnUrl="/flags/4x3/"
                                                                        cdnSuffix="svg"
                                                                        style={{ width: "1em", height: "1em" }}
                                                                    />
                                                                    <Typography variant="body2">
                                                                        {option.name} <Typography component="span" variant="caption" color="text.secondary">({option.nativeName})</Typography>
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                            renderTags={(value: Language[], getTagProps) =>
                                                                value.map((option, index) => {
                                                                    const tagProps = getTagProps({ index }) as any;
                                                                    const { key, ...rest } = tagProps;
                                                                    return (
                                                                        <Chip
                                                                            key={key}
                                                                            size="small"
                                                                            variant="outlined"
                                                                            icon={
                                                                                <ReactCountryFlag
                                                                                    countryCode={getLanguageFlagCountry(option.code)}
                                                                                    svg
                                                                                    cdnUrl="/flags/4x3/"
                                                                                    cdnSuffix="svg"
                                                                                    style={{ width: "1em", height: "1em" }}
                                                                                />
                                                                            }
                                                                            label={option.code.toUpperCase()}
                                                                            {...rest}
                                                                        />
                                                                    );
                                                                })
                                                            }
                                                            renderInput={(params) => <TextField {...params} label="Languages" placeholder="Select languages" />}
                                                        />
                                                    ) : (
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                                                                Languages
                                                            </Typography>
                                                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                                {(formData.languages || []).length > 0 ? (
                                                                    (formData.languages || []).map((lc) => (
                                                                        <Chip
                                                                            key={lc}
                                                                            size="small"
                                                                            icon={
                                                                                <ReactCountryFlag
                                                                                    countryCode={getLanguageFlagCountry(lc)}
                                                                                    svg
                                                                                    cdnUrl="/flags/4x3/"
                                                                                    cdnSuffix="svg"
                                                                                    style={{ width: "1em", height: "1em" }}
                                                                                />
                                                                            }
                                                                            label={(lc || "").toUpperCase()}
                                                                        />
                                                                    ))
                                                                ) : (
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        No languages set
                                                                    </Typography>
                                                                )}
                                                            </Stack>
                                                        </Box>
                                                    )}
                                                </Grid>
                                            </Grid>
                                        </Paper>

                                        {/* Notes/Memo section on Overview */}
                                        <Paper
                                            variant="outlined"
                                            sx={{
                                                p: 2,
                                                borderRadius: 2,
                                                borderColor: alpha(theme.palette.divider, theme.palette.mode === "dark" ? 0.4 : 1),
                                            }}
                                        >
                                            <Typography variant="subtitle1" fontWeight={900}>
                                                Notes
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                Internal notes and remarks about this worker.
                                            </Typography>
                                            {isNewWorker ? (
                                                <Typography variant="body2" color="text.secondary">
                                                    Create the worker first to add notes.
                                                </Typography>
                                            ) : (
                                                <WorkerMemo
                                                    memo={formData.memo || null}
                                                    disabled={!canManageWorkers}
                                                    onSave={async (memo) => {
                                                        if (!workerId || !canManageWorkers) return;
                                                        const mutationTicket = workerRequestFence.current.beginMutation(workerId);
                                                        if (!mutationTicket) return;
                                                        try {
                                                            setField("memo", memo);
                                                            const updated = await updateWorkerRequest({ workerId, memo });
                                                            if (!workerRequestFence.current.isCurrent(mutationTicket)) return;
                                                            setWorker(updated);
                                                            setOriginalFormData((prev) => ({ ...prev, memo }));
                                                            setSnackbar({ open: true, message: "Memo saved.", severity: "success" });
                                                        } catch (error) {
                                                            if (!workerRequestFence.current.isCurrent(mutationTicket)) return;
                                                            const message = extractErrorMessageSync(error);
                                                            setSnackbar({ open: true, message, severity: "error" });
                                                        }
                                                    }}
                                                />
                                            )}
                                        </Paper>
                                    </Stack>
                                </Grid>

                                {/* Right column - Position, Ownership */}
                                <Grid item xs={12} md={6}>
                                    <Stack spacing={2}>
                                        <Paper
                                            variant="outlined"
                                            sx={{
                                                p: 2,
                                                borderRadius: 2,
                                                borderColor: alpha(theme.palette.divider, theme.palette.mode === "dark" ? 0.4 : 1),
                                            }}
                                        >
                                            <Typography variant="subtitle1" fontWeight={900}>
                                                Position & status
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                Profession, workflow status and availability.
                                            </Typography>

                                            {editingAllowed && (
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={formData.isCustomOccupation}
                                                            onChange={(_e, checked) => setField("isCustomOccupation", checked)}
                                                        />
                                                    }
                                                    label="Custom occupation"
                                                />
                                            )}

                                            <Stack spacing={2}>
                                                {editingAllowed ? (
                                                    formData.isCustomOccupation ? (
                                                        <TextField
                                                            label="Custom occupation"
                                                            value={formData.customOccupation}
                                                            onChange={(e) => setField("customOccupation", e.target.value)}
                                                            fullWidth
                                                            size="small"
                                                            error={Boolean(formErrors.customOccupation)}
                                                            helperText={formErrors.customOccupation}
                                                        />
                                                    ) : (
                                                        <Autocomplete
                                                            size="small"
                                                            options={settings?.professions || []}
                                                            value={selectedProfession}
                                                            getOptionLabel={(p) => p.professionName}
                                                            onChange={(_e, p) => setField("professionKey", p?.professionKey || "")}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    label="Profession"
                                                                    error={Boolean(formErrors.professionKey)}
                                                                    helperText={formErrors.professionKey}
                                                                />
                                                            )}
                                                        />
                                                    )
                                                ) : (
                                                    <TextField
                                                        label="Profession"
                                                        value={
                                                            formData.isCustomOccupation
                                                                ? formData.customOccupation || "Custom"
                                                                : selectedProfession?.professionName || formData.professionKey || ""
                                                        }
                                                        fullWidth
                                                        size="small"
                                                        InputProps={{ readOnly: true }}
                                                    />
                                                )}

                                                <Grid container spacing={2}>
                                                    <Grid item xs={6}>
                                                        {editingAllowed ? (
                                                            <WorkerStatusEditor
                                                                value={formData.status}
                                                                error={Boolean(formErrors.status)}
                                                                onChange={handleStatusChange}
                                                                canManageWorkers={canManageWorkers}
                                                            />
                                                        ) : (
                                                            <TextField label="Status" value={formData.status} fullWidth size="small" InputProps={{ readOnly: true }} />
                                                        )}
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        {editingAllowed ? (
                                                            <FormControl fullWidth size="small">
                                                                <InputLabel>Availability</InputLabel>
                                                                <Select
                                                                    label="Availability"
                                                                    value={formData.workerAvailability}
                                                                    onChange={(e) =>
                                                                        setField("workerAvailability", e.target.value as WorkerAvailabilityStatus)
                                                                    }
                                                                >
                                                                    {Object.values(WorkerAvailabilityStatus).map((v) => (
                                                                        <MenuItem key={v} value={v}>
                                                                            {v}
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>
                                                        ) : (
                                                            <TextField
                                                                label="Availability"
                                                                value={formData.workerAvailability}
                                                                fullWidth
                                                                size="small"
                                                                InputProps={{ readOnly: true }}
                                                            />
                                                        )}
                                                    </Grid>
                                                </Grid>
                                            </Stack>
                                        </Paper>

                                        <RecruitmentPreferencesSection value={formData} editMode={editingAllowed} onChange={setField} />

                                        <Paper
                                            variant="outlined"
                                            sx={{
                                                p: 2,
                                                borderRadius: 2,
                                                borderColor: alpha(theme.palette.divider, theme.palette.mode === "dark" ? 0.4 : 1),
                                            }}
                                        >
                                            <Typography variant="subtitle1" fontWeight={900}>
                                                Ownership
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                Assign recruiter and account manager.
                                            </Typography>

                                            <Stack spacing={2}>
                                                {editingAllowed ? (
                                                    <Autocomplete
                                                        size="small"
                                                        options={recruiters}
                                                        value={selectedRecruiter}
                                                        onChange={(_e, u) => setField("recruiterId", u ? u.id : null)}
                                                        getOptionLabel={(u) => formatUserLabel(u)}
                                                        renderInput={(params) => <TextField {...params} label="Recruiter" />}
                                                        isOptionEqualToValue={(o, v) => o.id === v.id}
                                                    />
                                                ) : (
                                                    <TextField
                                                        label="Recruiter"
                                                        value={selectedRecruiter ? formatUserLabel(selectedRecruiter) : ""}
                                                        fullWidth
                                                        size="small"
                                                        InputProps={{ readOnly: true }}
                                                    />
                                                )}

                                                {editingAllowed ? (
                                                    <Autocomplete
                                                        size="small"
                                                        options={accountManagers}
                                                        value={selectedAccountManager}
                                                        onChange={(_e, u) => setField("accountManagerId", u ? u.id : null)}
                                                        getOptionLabel={(u) => formatUserLabel(u)}
                                                        renderInput={(params) => <TextField {...params} label="Account manager" />}
                                                        isOptionEqualToValue={(o, v) => o.id === v.id}
                                                    />
                                                ) : (
                                                    <TextField
                                                        label="Account manager"
                                                        value={selectedAccountManager ? formatUserLabel(selectedAccountManager) : ""}
                                                        fullWidth
                                                        size="small"
                                                        InputProps={{ readOnly: true }}
                                                    />
                                                )}
                                            </Stack>
                                        </Paper>

                                    </Stack>
                                </Grid>

                            </Grid>
                        </TabPanel>

                        {/* Questions tab */}
                        <TabPanel value={tab} index={1}>
                            {isNewWorker ? (
                                disabledPanel("Questions", "Create the worker first to view questions.")
                            ) : (
                                <WorkerQuestionForm workerId={workerId} worker={worker ?? undefined} />
                            )}
                        </TabPanel>

                        {/* Documents tab - combines Files and Identification */}
                        <TabPanel value={tab} index={2}>
                            {isNewWorker ? (
                                disabledPanel("Documents", "Create the worker first to manage files and documents.")
                            ) : (
                                <Grid container spacing={2}>
                                    {/* Document-expiry warning banner. Renders only when VCA or
                                        ID expiry is within 3 months (or already expired). Pure
                                        client-side computation — no fetch. */}
                                    <Grid item xs={12}>
                                        <DocumentExpiryBanner
                                            vcaValidUntil={worker?.vcaValidUntil ?? null}
                                            idDocumentValidUntil={worker?.idDocumentValidUntil ?? null}
                                        />
                                    </Grid>
                                    {/* NAW document section. Disabled-button + info-icon UX
                                        comes from the readiness endpoint. onGenerated
                                        bumps filesRefreshKey so the new NAW_DOCUMENT row
                                        appears in the file list immediately. */}
                                    <Grid item xs={12}>
                                        <NawDocumentSection
                                            workerId={workerId}
                                            onGenerated={() => setFilesRefreshKey((k) => k + 1)}
                                        />
                                    </Grid>
                                    {/* Recruiter-editable NAW fields (bsn / idDocument* /
                                        vca* / homeAddress). Same fields the bot
                                        writes via VCA/ID upload confirmation; shown here
                                        so the recruiter can fill gaps without uploading. */}
                                    {worker && (
                                        <Grid item xs={12}>
                                            <NawFieldsEditor worker={worker} onSaved={loadWorker} />
                                        </Grid>
                                    )}
                                    <Grid item xs={12}>
                                        <Paper
                                            variant="outlined"
                                            sx={{
                                                p: 2,
                                                borderRadius: 2,
                                                borderColor: alpha(theme.palette.divider, theme.palette.mode === "dark" ? 0.4 : 1),
                                            }}
                                        >
                                            <Typography variant="subtitle1" fontWeight={900} sx={{ mb: 2 }}>
                                                Files
                                            </Typography>
                                            <WorkerFileManager workerId={workerId} workerNumericId={worker?.id} refreshKey={filesRefreshKey} />
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Paper
                                            variant="outlined"
                                            sx={{
                                                p: 2,
                                                borderRadius: 2,
                                                borderColor: alpha(theme.palette.divider, theme.palette.mode === "dark" ? 0.4 : 1),
                                            }}
                                        >
                                            <Typography variant="subtitle1" fontWeight={900} sx={{ mb: 2 }}>
                                                Identification documents
                                            </Typography>
                                            <IdentificationDocuments workerId={workerId} onUpdated={loadWorker} />
                                        </Paper>
                                    </Grid>
                                    {worker && (
                                        <Grid item xs={12}>
                                            <Paper
                                                variant="outlined"
                                                sx={{
                                                    p: 2,
                                                    borderRadius: 2,
                                                    borderColor: alpha(theme.palette.divider, theme.palette.mode === "dark" ? 0.4 : 1),
                                                }}
                                            >
                                                <WorkerCertifications workerNumericId={worker.id} />
                                            </Paper>
                                        </Grid>
                                    )}
                                </Grid>
                            )}
                        </TabPanel>

                        {/* Experience tab — work history + education (read-only). */}
                        <TabPanel value={tab} index={3}>
                            {isNewWorker ? (
                                disabledPanel("Experience", "Create the worker first to view experience.")
                            ) : worker ? (
                                <WorkerExperience worker={worker} />
                            ) : null}
                        </TabPanel>

                        <TabPanel value={tab} index={4}>
                            {isNewWorker ? (
                                disabledPanel("EasyFlex", "Create the worker first to view EasyFlex details.")
                            ) : (
                                <EasyflexProfilePanel
                                    easyflexRegistrationNumber={worker?.easyflexRegistrationNumber}
                                    easyflexOrganization={worker?.easyflexOrganization}
                                />
                            )}
                        </TabPanel>

                        <TabPanel value={tab} index={5}>
                            {isNewWorker ? (
                                disabledPanel(
                                    "EasyFlex Docs",
                                    "Create the worker first to view their mirrored EasyFlex documents.",
                                )
                            ) : (
                                <EasyflexDocumentsTab
                                    easyflexRegistrationNumber={
                                        worker?.easyflexRegistrationNumber
                                            ? Number(worker.easyflexRegistrationNumber)
                                            : null
                                    }
                                    organization={worker?.easyflexOrganization || undefined}
                                />
                            )}
                        </TabPanel>

                        <TabPanel value={tab} index={6}>
                            {isNewWorker ? (
                                disabledPanel("Channels", "Create the worker first to manage communication channels.")
                            ) : (
                                <CommunicationChannelsList workerId={workerId} />
                            )}
                        </TabPanel>

                        {/* Certificate Review — per-worker approve/reject of certs. */}
                        <TabPanel value={tab} index={7}>
                            {isNewWorker ? (
                                disabledPanel("Certificate Review", "Create the worker first to review certificates.")
                            ) : worker ? (
                                <WorkerCertReview workerNumericId={worker.id} />
                            ) : null}
                        </TabPanel>
                        {canManageWorkers && (
                            <TabPanel value={tab} index={WORKER_DETAIL_TAB.CONTACT}>
                                <WorkerContactTimeline workerId={workerId} canManageWorkers />
                            </TabPanel>
                        )}
                        {canManageWorkers && (
                            <TabPanel value={tab} index={WORKER_DETAIL_TAB.FEEDBACK}>
                                <WorkerFeedbackPanel workerId={workerId} canManageWorkers />
                            </TabPanel>
                        )}
                        <TabPanel value={tab} index={WORKER_DETAIL_TAB.HISTORY}>
                            {workerId && <WorkerAuditTimeline workerId={workerId} />}
                        </TabPanel>
                    </>
                )}
            </Stack>

            <ConfirmationModal
                open={showDeleteModal && canManageWorkers}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleDelete}
                message="Are you sure you want to delete this worker?"
            />

            <CVEditorModal
                open={showCVEditor}
                onClose={() => setShowCVEditor(false)}
                workerId={workerId}
                workerName={`${formData.firstName || ''} ${formData.lastName || ''}`.trim() || 'Worker'}
                includePersonalInfo={true}
                onWorkerDataChanged={loadWorker}
            />

            <CandidateConfirmationDialog
                open={showCandidateDialog && canManageWorkers}
                onClose={handleCandidateDialogClose}
                onConfirm={handleCandidateConfirm}
                workerName={displayName}
                isLoading={saving}
            />

            {canManageWorkers && (
                <WorkerSaleslistPushDialog
                    open={showSaleslistDialog}
                    workerId={workerId}
                    workerName={displayName}
                    onClose={() => setShowSaleslistDialog(false)}
                    onQueued={(message) =>
                        setSnackbar({ open: true, message, severity: "success" })
                    }
                />
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default WorkerDetail;
